import type { ColDef as BaseColDef, ICellRendererFunc, ICellRendererParams } from 'ag-grid-community'
import type { ColDefField } from 'ag-grid-community/dist/lib/entities/colDef'
// biome-ignore lint/suspicious/noExplicitAny: Mirrors base column definition
type ColDef<T = any, V = any> = Omit<BaseColDef<T, V>, 'cellRenderer' | 'field'> & {
  // biome-ignore lint/suspicious/noExplicitAny: Mirrors base column definition
  cellRenderer?: string | ((params: ICellRendererParams<T, V, unknown>) => any)
}

/** utility type to remove index types (e.g [key: string]: any) from a given type,
 since they break key inferrence */
type RemoveIndex<T> = {
  [K in keyof T as string extends K ? never : number extends K ? never : symbol extends K ? never : K]: T[K]
}

/**gets the value type based on a string path with dot-notation*/
type GetTypeByPath<T, V, P extends ColDefField<T, V>> = P extends keyof T
  ? T[P]
  : P extends `${infer K}.${infer Rest}`
    ? K extends keyof T
      ? GetTypeByPath<T[K], V, Rest extends ColDefField<T[K], V> ? Rest : never>
      : never
    : never

type BuilderResult<T, R extends Array<unknown> = never[]> = R extends never[] ? [T] : [...R, T]

/**
 * Can be used to define ag-grid columns in a type-safe way
 */
export class ColDefBuilder<T, R extends Array<unknown> = never[], TF = RemoveIndex<T>> {
  private _defaults: Partial<ColDef<TF>> = {}
  private _columns = [] as Array<ColDef<TF> & { field?: string }>
  constructor(defaults: Partial<ColDef<TF>> = {}) {
    this._defaults = defaults
  }

  defineColumn<TBP, K extends ColDefField<TF, TBP>, V = GetTypeByPath<TF, TBP, K>>(
    key: K,
    definition: ColDef<TF, V>
  ): ColDefBuilder<T, BuilderResult<ColDef<TF, V>, R>, TF> {
    this._columns.push({ ...this._defaults, ...definition, field: key })
    return this as unknown as ColDefBuilder<T, BuilderResult<ColDef<TF, V>, R>, TF>
  }

  /** used for columns that are not based on an object field */
  defineUnknownColumn<V>(definition: ColDef<TF, V>): ColDefBuilder<T, BuilderResult<ColDef<TF, V>, R>, TF> {
    this._columns.push({ ...this._defaults, ...definition })
    return this as unknown as ColDefBuilder<T, BuilderResult<ColDef<TF, V>, R>, TF>
  }

  get columns(): Array<BaseColDef<T>> {
    return this._columns as unknown as Array<BaseColDef<T>>
  }
}
